import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import { NUVEI_PAYMENT_SESSION_REFRESH_MS } from '@/constants/nuvei';
import { useExpressReveal } from '@/context/ExpressRevealContext';
import { useExpressWalletFallback } from '@/hooks/useExpressWalletFallback';
import { useLatestRef } from '@/hooks/useLatestRef';
import { ProviderEnum } from '@/types/common';
import type { WalletReadyState } from '@/components/Wallets/WalletButton/WalletButtonShell';
import { logError } from '@/utils/logger';
import { isNuveiPaymentSessionFresh, prefetchNuveiSessionForCurrentBinding } from '@/utils/nuvei';

type NuveiWallet = 'apple_pay' | 'google_pay';

const NUVEI_WALLET_REFRESH_RETRY_BASE_MS = 1_000;
const NUVEI_WALLET_REFRESH_RETRY_MAX_MS = 30_000;

type PrefetchedSessionMetadata = {
  createdAt: number;
  bindingKey: string;
};

export type PrefetchedNuveiSession<TSession extends object> = TSession & PrefetchedSessionMetadata;

type UseNuveiWalletLifecycleParams<TSession extends object> = {
  wallet: NuveiWallet;
  bindingKey: string;
  fetchSession: () => Promise<TSession | null>;
  prefetchErrorMessage: string;
  isPaymentRequestSupported?: boolean;
  /**
   * Session prerequisites (e.g. a valid shopper email for `receipt_email`).
   * When false the wallet may still settle `ready` — prefetch is skipped
   * (no doomed session calls) and resumes automatically once it flips true.
   * Unlike `isPaymentRequestSupported=false`, this does NOT mark the wallet
   * unavailable.
   */
  canCreateSession?: boolean;
  onReady?: (isAvailable: boolean) => void;
};

type UseNuveiWalletLifecycleResult<TSession extends object> = {
  state: WalletReadyState;
  setState: Dispatch<SetStateAction<WalletReadyState>>;
  prefetchRef: MutableRefObject<PrefetchedNuveiSession<TSession> | null>;
  prefetchSession: () => Promise<PrefetchedNuveiSession<TSession> | null>;
  refreshPaymentReadiness: (options?: { silent?: boolean }) => Promise<PrefetchedNuveiSession<TSession> | null>;
  isPaymentReady: boolean;
  buttonState: WalletReadyState;
  isPayingRef: MutableRefObject<boolean>;
  settleAvailability: (isAvailable: boolean, reason?: string) => void;
  revealWallet: () => void;
};

export const useNuveiWalletLifecycle = <TSession extends object>({
  wallet,
  bindingKey,
  fetchSession,
  prefetchErrorMessage,
  isPaymentRequestSupported = true,
  canCreateSession = true,
  onReady,
}: UseNuveiWalletLifecycleParams<TSession>): UseNuveiWalletLifecycleResult<TSession> => {
  const [state, setState] = useState<WalletReadyState>('loading');
  const [isSessionRefreshing, setIsSessionRefreshing] = useState(false);
  const [isSessionRecovering, setIsSessionRecovering] = useState(false);
  // Bumped after successful session rebind so `isPaymentReady` recomputes when
  // `prefetchRef` changes without a visible wallet-state transition (silent ZIP).
  const [, setSessionEpoch] = useState(0);
  const isPayingRef = useRef(false);
  const settledRef = useRef(false);
  const hasReportedAvailableRef = useRef(false);
  const mountedRef = useRef(true);
  const mountTimeRef = useRef(Date.now());
  const prefetchRef = useRef<PrefetchedNuveiSession<TSession> | null>(null);
  const prefetchInFlightRef = useRef<Promise<PrefetchedNuveiSession<TSession> | null> | null>(null);
  const refreshInFlightRef = useRef<Promise<PrefetchedNuveiSession<TSession> | null> | null>(null);
  const refreshRetryTimeoutRef = useRef<number | null>(null);
  const refreshRetryAttemptRef = useRef(0);
  const wasPaymentRequestUnsupportedRef = useRef(false);
  const refreshPaymentReadinessRef = useRef<
    ((options?: { silent?: boolean }) => Promise<PrefetchedNuveiSession<TSession> | null>) | null
  >(null);
  const bindingKeyRef = useLatestRef(bindingKey);
  const fetchSessionRef = useLatestRef(fetchSession);
  const prefetchErrorMessageRef = useLatestRef(prefetchErrorMessage);
  const isPaymentRequestSupportedRef = useLatestRef(isPaymentRequestSupported);
  const canCreateSessionRef = useLatestRef(canCreateSession);
  const onReadyRef = useLatestRef(onReady);
  const reportWalletAvailabilityRef = useLatestRef(useExpressWalletFallback());
  const expressRevealRef = useLatestRef(useExpressReveal());

  const settleAvailability = useCallback(
    (isAvailable: boolean, reason?: string) => {
      // First settle wins, except one upgrade: early init may settle
      // unavailable (missing orderId/config), then succeed once deps arrive.
      if (settledRef.current) {
        if (!(isAvailable && !hasReportedAvailableRef.current)) {
          return;
        }
      }
      settledRef.current = true;
      hasReportedAvailableRef.current = isAvailable;
      reportWalletAvailabilityRef.current(isAvailable, {
        wallet,
        provider: ProviderEnum.NUVEI,
        reason: isAvailable ? undefined : reason,
        loadTimeMs: Date.now() - mountTimeRef.current,
      });
      onReadyRef.current?.(isAvailable);
    },
    [onReadyRef, reportWalletAvailabilityRef, wallet]
  );

  const revealWallet = useCallback(() => {
    expressRevealRef.current?.onWalletReady();
  }, [expressRevealRef]);

  const prefetchSession = useCallback(async (): Promise<PrefetchedNuveiSession<TSession> | null> => {
    if (!isPaymentRequestSupportedRef.current || !canCreateSessionRef.current) {
      prefetchRef.current = null;
      return null;
    }
    if (prefetchInFlightRef.current) {
      return prefetchInFlightRef.current;
    }

    const task = (async () => {
      const requestedBindingKey = bindingKeyRef.current;
      try {
        const session = await fetchSessionRef.current();
        if (!session || !isPaymentRequestSupportedRef.current) {
          prefetchRef.current = null;
          return null;
        }

        const prefetched: PrefetchedNuveiSession<TSession> = {
          ...session,
          createdAt: Date.now(),
          bindingKey: requestedBindingKey,
        };
        prefetchRef.current = prefetched;
        return prefetched;
      } catch (error) {
        logError(prefetchErrorMessageRef.current, error);
        prefetchRef.current = null;
        return null;
      } finally {
        prefetchInFlightRef.current = null;
      }
    })();

    prefetchInFlightRef.current = task;
    return task;
  }, [bindingKeyRef, canCreateSessionRef, fetchSessionRef, isPaymentRequestSupportedRef, prefetchErrorMessageRef]);

  const scheduleRefreshRetry = useCallback(() => {
    if (
      !mountedRef.current ||
      !hasReportedAvailableRef.current ||
      !isPaymentRequestSupportedRef.current ||
      !canCreateSessionRef.current ||
      refreshRetryTimeoutRef.current !== null
    ) {
      return;
    }

    const retryDelay = Math.min(
      NUVEI_WALLET_REFRESH_RETRY_BASE_MS * 2 ** Math.min(refreshRetryAttemptRef.current, 5),
      NUVEI_WALLET_REFRESH_RETRY_MAX_MS
    );
    refreshRetryAttemptRef.current += 1;
    refreshRetryTimeoutRef.current = window.setTimeout(() => {
      refreshRetryTimeoutRef.current = null;
      if (
        !mountedRef.current ||
        isPayingRef.current ||
        !isPaymentRequestSupportedRef.current ||
        !canCreateSessionRef.current
      ) {
        return;
      }
      void refreshPaymentReadinessRef.current?.();
    }, retryDelay);
  }, [canCreateSessionRef, isPaymentRequestSupportedRef]);

  const refreshPaymentReadiness = useCallback(
    (options?: { silent?: boolean }): Promise<PrefetchedNuveiSession<TSession> | null> => {
      const silent = options?.silent === true;
      if (!isPaymentRequestSupportedRef.current) {
        prefetchRef.current = null;
        if (mountedRef.current) {
          setIsSessionRecovering(false);
          setIsSessionRefreshing(false);
          setState('unavailable');
        }
        return Promise.resolve(null);
      }
      if (!canCreateSessionRef.current) {
        // Session prerequisites missing (e.g. no valid shopper email yet):
        // stand down without entering recovery — the rebind effect resumes the
        // prefetch when `canCreateSession` flips true.
        prefetchRef.current = null;
        if (mountedRef.current) {
          setIsSessionRecovering(false);
          setIsSessionRefreshing(false);
          // A cancelled/failed no-email pay attempt leaves the wallet's state
          // at 'loading' (onPay set it before opening the sheet). Restore
          // 'ready' for an already-available wallet so the button stays
          // tappable — otherwise it is stuck loading forever (the resume
          // effect only runs while state === 'ready').
          if (hasReportedAvailableRef.current) {
            setState('ready');
          }
        }
        return Promise.resolve(null);
      }
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }
      if (refreshRetryTimeoutRef.current !== null) {
        window.clearTimeout(refreshRetryTimeoutRef.current);
        refreshRetryTimeoutRef.current = null;
      }

      // Silent = amount/currency rebind (e.g. ZIP tax). Keep the wallet shell
      // visually ready; ZipCode already shows the clean prefix loader. Hard
      // refresh still paints the wallet loading overlay.
      if (mountedRef.current && !silent) {
        setIsSessionRefreshing(true);
      }
      // Keep the previous prefetch during silent refresh so the button does not
      // flap through !isPaymentReady → loading. Clicks still gate on binding
      // match + freshness in the wallet onPay handlers.
      if (!silent) {
        prefetchRef.current = null;
      }

      const task = (async () => {
        try {
          const prefetched = await prefetchNuveiSessionForCurrentBinding(
            prefetchSession,
            () => bindingKeyRef.current,
            () => {
              prefetchRef.current = null;
            }
          );
          if (!isPaymentRequestSupportedRef.current) {
            prefetchRef.current = null;
            return null;
          }
          if (!mountedRef.current) {
            return prefetched;
          }
          if (prefetched) {
            refreshRetryAttemptRef.current = 0;
            setIsSessionRecovering(false);
            setState('ready');
            setSessionEpoch((epoch) => epoch + 1);
          } else if (!canCreateSessionRef.current) {
            // Prerequisites disappeared mid-flight (e.g. shopper cleared the
            // email). Not a session failure — no recovery loop, keep state.
            setIsSessionRecovering(false);
          } else if (hasReportedAvailableRef.current) {
            setIsSessionRecovering(true);
            setState('ready');
            scheduleRefreshRetry();
          } else {
            setIsSessionRecovering(false);
            setState('unavailable');
          }
          return prefetched;
        } finally {
          refreshInFlightRef.current = null;
          if (mountedRef.current && !silent) {
            setIsSessionRefreshing(false);
          }
        }
      })();

      refreshInFlightRef.current = task;
      return task;
    },
    [bindingKeyRef, canCreateSessionRef, isPaymentRequestSupportedRef, prefetchSession, scheduleRefreshRetry]
  );
  refreshPaymentReadinessRef.current = refreshPaymentReadiness;

  useEffect(() => {
    if (!isPaymentRequestSupported) {
      wasPaymentRequestUnsupportedRef.current = true;
      prefetchRef.current = null;
      refreshRetryAttemptRef.current = 0;
      if (refreshRetryTimeoutRef.current !== null) {
        window.clearTimeout(refreshRetryTimeoutRef.current);
        refreshRetryTimeoutRef.current = null;
      }
      setIsSessionRecovering(false);
      setIsSessionRefreshing(false);
      setState('unavailable');
      return;
    }

    if (wasPaymentRequestUnsupportedRef.current) {
      wasPaymentRequestUnsupportedRef.current = false;
      void refreshPaymentReadiness();
    }
  }, [isPaymentRequestSupported, refreshPaymentReadiness]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (refreshRetryTimeoutRef.current !== null) {
        window.clearTimeout(refreshRetryTimeoutRef.current);
        refreshRetryTimeoutRef.current = null;
      }
    };
  }, []);

  // Nuvei openOrder is amount/currency-bound. When tax (ZIP) changes the total,
  // silently rebind the session — do not remount / overlay-reload the wallet
  // buttons (ZipCode owns the clean loader). Also resumes the deferred prefetch
  // when `canCreateSession` flips true (e.g. the shopper typed a valid email).
  useEffect(() => {
    if (!isPaymentRequestSupported || !canCreateSession || state !== 'ready') {
      return;
    }
    if (prefetchRef.current?.bindingKey === bindingKey && isNuveiPaymentSessionFresh(prefetchRef.current.createdAt)) {
      return;
    }
    void refreshPaymentReadiness({ silent: true });
  }, [state, bindingKey, isPaymentRequestSupported, canCreateSession, refreshPaymentReadiness]);

  const isPaymentReady =
    isPaymentRequestSupported &&
    state === 'ready' &&
    !isSessionRefreshing &&
    prefetchRef.current?.bindingKey === bindingKey &&
    isNuveiPaymentSessionFresh(prefetchRef.current.createdAt);
  // Once available, keep the shell visually ready during silent amount rebinds.
  // Hard refresh / recovery still use the wallet loading overlay.
  const buttonState: WalletReadyState =
    isSessionRefreshing || isSessionRecovering
      ? 'loading'
      : state === 'ready' && !isPaymentReady && !hasReportedAvailableRef.current
        ? 'loading'
        : state;

  useEffect(() => {
    const prefetched = prefetchRef.current;
    if (!isPaymentReady || !prefetched) {
      return;
    }

    const refreshDelay = Math.max(0, NUVEI_PAYMENT_SESSION_REFRESH_MS - (Date.now() - prefetched.createdAt));
    const timeout = window.setTimeout(() => {
      if (!isPayingRef.current) {
        void refreshPaymentReadiness();
      }
    }, refreshDelay);

    return () => window.clearTimeout(timeout);
  }, [isPaymentReady, refreshPaymentReadiness]);

  return {
    state,
    setState,
    prefetchRef,
    prefetchSession,
    refreshPaymentReadiness,
    isPaymentReady,
    buttonState,
    isPayingRef,
    settleAvailability,
    revealWallet,
  };
};
